import React from "react";
import { Submission, PartSubmission, GuideSubmission } from "@/types/submission";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface SubmissionDetailsProps {
  submission: Submission;
}

// Define tutorial submission type
interface TutorialSubmission {
  name: string;
  description: string;
  coverImage?: string;
  tags?: string[];
  difficulty?: string;
  bom?: Array<{
    id: string;
    name: string;
    quantity: number;
    category?: string;
    externalLink?: string;
  }>;
  sections?: Array<{
    id: string;
    title: string;
    steps: Array<{
      id: string;
      text: string;
      images: string[];
      completed: boolean;
    }>;
    collapsed: boolean;
  }>;
}

const SubmissionDetails = ({ submission }: SubmissionDetailsProps) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const renderPartDetails = (part: PartSubmission) => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium mb-1">Name</p>
            <p className="text-sm text-muted-foreground">{part.name}</p>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Category</p>
            <p className="text-sm text-muted-foreground capitalize">{part.category || "Not specified"}</p>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Subcategory</p>
            <p className="text-sm text-muted-foreground capitalize">{part.subcategory}</p>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Manufacturer</p>
            <p className="text-sm text-muted-foreground">{part.manufacturer || "Not specified"}</p>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Difficulty</p>
            <p className="text-sm text-muted-foreground">{part.difficulty} / 5</p>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Rating</p>
            <p className="text-sm text-muted-foreground">{part.rating} / 5</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium mb-2">Description</h3>
        <p className="text-sm whitespace-pre-wrap">{part.description}</p>
      </div>
      
      <div>
        <h3 className="text-lg font-medium mb-2">Filters/Tags</h3>
        <div className="flex flex-wrap gap-2">
          {part.filters?.map((filter, index) => (
            <Badge key={index} variant="outline">
              {filter}
            </Badge>
          ))}
        </div>
      </div>
      
      <div>
        <h3 className="text-lg font-medium mb-2">Links</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {part.amazon_link && (
            <div>
              <p className="text-sm font-medium mb-1">Amazon</p>
              <a href={part.amazon_link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">
                {part.amazon_link}
              </a>
            </div>
          )}
          {part.ali_express_link && (
            <div>
              <p className="text-sm font-medium mb-1">AliExpress</p>
              <a href={part.ali_express_link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">
                {part.ali_express_link}
              </a>
            </div>
          )}
          {part.video_guide && (
            <div>
              <p className="text-sm font-medium mb-1">Video Guide</p>
              <a href={part.video_guide} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">
                {part.video_guide}
              </a>
            </div>
          )}
          {part.written_guide && (
            <div>
              <p className="text-sm font-medium mb-1">Written Guide</p>
              <a href={part.written_guide} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">
                {part.written_guide}
              </a>
            </div>
          )}
        </div>
      </div>
      
      <div>
        <h3 className="text-lg font-medium mb-2">Requirements</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium mb-1">Requires Wiring</p>
            <p className="text-sm text-muted-foreground">{part.requires_wiring ? "Yes" : "No"}</p>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Requires Root</p>
            <p className="text-sm text-muted-foreground">{part.requires_root ? "Yes" : "No"}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGuideDetails = (guide: GuideSubmission) => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium mb-1">Title</p>
            <p className="text-sm text-muted-foreground">{guide.name}</p>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Category</p>
            <p className="text-sm text-muted-foreground capitalize">{guide.category}</p>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Subcategory</p>
            <p className="text-sm text-muted-foreground capitalize">{guide.subcategory}</p>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Difficulty</p>
            <p className="text-sm text-muted-foreground capitalize">{guide.difficulty}</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium mb-2">Description</h3>
        <p className="text-sm whitespace-pre-wrap">{guide.description}</p>
      </div>
      
      <div>
        <h3 className="text-lg font-medium mb-2">Tags</h3>
        <div className="flex flex-wrap gap-2">
          {guide.filters?.map((filter, index) => (
            <Badge key={index} variant="outline">
              {filter}
            </Badge>
          ))}
        </div>
      </div>
      
      <div>
        <h3 className="text-lg font-medium mb-2">Steps</h3>
        <div className="space-y-4">
          {guide.steps?.map((step, index) => (
            <Card key={index}>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Step {index + 1}: {step.title}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm whitespace-pre-wrap">{step.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      
      <div>
        <h3 className="text-lg font-medium mb-2">Links</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {guide.videoLink && (
            <div>
              <p className="text-sm font-medium mb-1">Video Guide</p>
              <a href={guide.videoLink} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">
                {guide.videoLink}
              </a>
            </div>
          )}
          {guide.writtenGuide && (
            <div>
              <p className="text-sm font-medium mb-1">Additional Written Guide</p>
              <a href={guide.writtenGuide} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">
                {guide.writtenGuide}
              </a>
            </div>
          )}
        </div>
      </div>
      
      <div>
        <h3 className="text-lg font-medium mb-2">Requirements</h3>
        <div>
          <p className="text-sm font-medium mb-1">Requires Root</p>
          <p className="text-sm text-muted-foreground">{guide.requires_root ? "Yes" : "No"}</p>
        </div>
      </div>
    </div>
  );

  const renderTutorialDetails = (tutorial: TutorialSubmission) => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium mb-1">Title</p>
            <p className="text-sm text-muted-foreground">{tutorial.name}</p>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Difficulty</p>
            <p className="text-sm text-muted-foreground">{tutorial.difficulty || "Not specified"}</p>
          </div>
        </div>
      </div>

      {tutorial.coverImage && (
        <div>
          <h3 className="text-lg font-medium mb-2">Cover Image</h3>
          <img 
            src={tutorial.coverImage} 
            alt="Tutorial cover" 
            className="max-w-md rounded-md border"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      )}

      <div>
        <h3 className="text-lg font-medium mb-2">Description</h3>
        <p className="text-sm whitespace-pre-wrap">{tutorial.description}</p>
      </div>
      
      {tutorial.tags && tutorial.tags.length > 0 && (
        <div>
          <h3 className="text-lg font-medium mb-2">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {tutorial.tags.map((tag, index) => (
              <Badge key={index} variant="outline">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {tutorial.bom && tutorial.bom.length > 0 && (
        <div>
          <h3 className="text-lg font-medium mb-2">Bill of Materials</h3>
          <div className="space-y-2">
            {tutorial.bom.map((item, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-muted-foreground">Quantity: {item.quantity}</p>
                      {item.category && (
                        <p className="text-sm text-muted-foreground">Category: {item.category}</p>
                      )}
                    </div>
                    {item.externalLink && (
                      <a 
                        href={item.externalLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Link
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
      
      {tutorial.sections && tutorial.sections.length > 0 && (
        <div>
          <h3 className="text-lg font-medium mb-2">Tutorial Sections</h3>
          <div className="space-y-4">
            {tutorial.sections.map((section, sectionIndex) => (
              <Card key={sectionIndex}>
                <CardHeader>
                  <CardTitle className="text-lg">{section.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  {section.steps && section.steps.length > 0 ? (
                    <div className="space-y-3">
                      {section.steps.map((step, stepIndex) => (
                        <div key={stepIndex} className="border-l-4 border-primary/20 pl-4">
                          <p className="font-medium text-sm">Step {stepIndex + 1}</p>
                          <p className="text-sm whitespace-pre-wrap">{step.text}</p>
                          {step.images && step.images.length > 0 && (
                            <div className="mt-2 flex gap-2 flex-wrap">
                              {step.images.map((image, imageIndex) => (
                                <img 
                                  key={imageIndex}
                                  src={image} 
                                  alt={`Step ${stepIndex + 1} image ${imageIndex + 1}`}
                                  className="w-20 h-20 object-cover rounded border"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No steps added yet.</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (submission.type) {
      case "part":
        return renderPartDetails(submission.content as PartSubmission);
      case "guide":
        return renderGuideDetails(submission.content as GuideSubmission);
      case "tutorial":
        return renderTutorialDetails(submission.content as TutorialSubmission);
      default:
        return (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Unknown submission type: {submission.type}</p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Badge variant="outline" className="capitalize">{submission.type}</Badge>
          <h2 className="text-xl font-semibold mt-2">{submission.content.name}</h2>
        </div>
        <Badge variant={
          submission.status === "pending" ? "outline" :
          submission.status === "approved" ? "secondary" : "destructive"
        } className="capitalize">
          {submission.status}
        </Badge>
      </div>
      
      <Separator />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <p className="font-medium">Submitted</p>
          <p className="text-muted-foreground">{formatDate(submission.submitted_at)}</p>
        </div>
        {submission.status === "approved" && submission.approved_at && (
          <div>
            <p className="font-medium">Approved</p>
            <p className="text-muted-foreground">{formatDate(submission.approved_at)}</p>
          </div>
        )}
      </div>
      
      {submission.feedback && (
        <div className="bg-muted p-4 rounded-md">
          <p className="font-medium mb-1">Feedback</p>
          <p className="text-sm whitespace-pre-wrap">{submission.feedback}</p>
        </div>
      )}
      
      <Separator />
      
      <div>
        {renderContent()}
      </div>
    </div>
  );
};

export default SubmissionDetails;
